import { useEffect, useMemo } from 'react'
import {
  useSpotSwapPair,
  useTokenlonConfig,
  useTokenlonV6NetworkConfig,
} from '~/state/dex/hooks'
import { useUpdaterChainId } from '~/hooks'
import { useLocalStorage } from '~/hooks/useLocalStorage'
import { isMainnetOrSepolia } from '~/utils/chain'

export type SlippageItem = {
  value: number
  userModified: boolean
}

export function useDefaultSlippage() {
  const chainId = useUpdaterChainId()
  const isMainnetOrSepoliaFlag = isMainnetOrSepolia(chainId)
  const [inputToken, outputToken] = useSpotSwapPair()

  // 获取 l1 slippage
  const { slippageConfig } = useTokenlonConfig()
  const l1DefaultSlippage = slippageConfig?.default || 1

  // 获取 l2 slippage, use bps, 200 means 2%
  const { slippage: l2NetworkDefaultSlippage } = useTokenlonV6NetworkConfig()
  const inputTokenSlippage =
    inputToken?.slippage || l2NetworkDefaultSlippage || 200
  const outputTokenSlippage =
    outputToken?.slippage || l2NetworkDefaultSlippage || 200

  const defaultSlippage = useMemo(() => {
    if (isMainnetOrSepoliaFlag) {
      return l1DefaultSlippage
    } else {
      const slippageBPS =
        inputTokenSlippage < outputTokenSlippage
          ? outputTokenSlippage
          : inputTokenSlippage
      return slippageBPS / 100
    }
  }, [
    isMainnetOrSepoliaFlag,
    l1DefaultSlippage,
    inputTokenSlippage,
    outputTokenSlippage,
  ])
  return defaultSlippage
}

export function useSlippage() {
  const chainId = useUpdaterChainId()

  // l1 slippage config
  const { slippageConfig } = useTokenlonConfig()

  const defaultSlippage = useDefaultSlippage()

  const [slippages, setSlippages] = useLocalStorage(`slippages`, {
    [chainId]: {
      value: defaultSlippage,
      userModified: false,
    },
  })

  const setSlippageItem = (chainId: number, slippageItem: SlippageItem) => {
    setSlippages({
      ...slippages,
      [chainId]: slippageItem,
    })
  }

  useEffect(() => {
    const slippageItem = slippages[chainId] as SlippageItem | undefined

    if (!slippageItem) {
      setSlippageItem(chainId, {
        value: defaultSlippage,
        userModified: false,
      })
    } else {
      const maxSlippage = slippageConfig?.max || 5
      const minSlippage = slippageConfig?.min || 0.1
      const slippage = slippageItem.value

      const isOutOfRange = slippage < minSlippage || slippage > maxSlippage
      const needUpdate =
        !slippageItem.userModified && slippage !== defaultSlippage

      if (isOutOfRange || needUpdate) {
        setSlippageItem(chainId, {
          value: defaultSlippage,
          userModified: false,
        })
      }
    }
  }, [chainId, defaultSlippage, slippageConfig])

  const slippageValue = slippages[chainId]?.value || defaultSlippage
  const slippageBPS = Math.round(slippageValue * 100)

  return { slippageBPS, slippageValue, setSlippageItem }
}
